import React, { useContext, useEffect, useLayoutEffect } from 'react';
import { ViewerChromeContext } from './ViewersLayout';

/**
 * Per-tab chrome for the /viewers section.
 *
 * The title, subtext, pill tabs and action buttons are rendered once by
 * <ViewersLayout>, which the section's route keeps mounted across tab switches.
 * This component no longer renders that chrome itself — it publishes the active
 * tab's configuration upward and renders the tab's content. Its props are
 * unchanged, so the tab pages did not have to move.
 */
export const ViewerPage = ({
  children, title, addActionName, link, onAddAction, subTitle, restricted,
  showCopy, onCopyClick, hideHeaderActions,
}) => {
  const chrome = useContext(ViewerChromeContext);

  // Every value here is a primitive, so this settles after one pass. Layout
  // effect, not a plain one: it runs before paint, so the incoming tab's title
  // is in place on the same frame and never flashes the previous tab's.
  useLayoutEffect(() => {
    chrome?.setChrome({
      title,
      subTitle,
      addActionName,
      link,
      restricted: !!restricted,
      hideHeaderActions: !!hideHeaderActions,
      showCopy: !!showCopy,
    });
  }, [title, subTitle, addActionName, link, restricted, hideHeaderActions, showCopy]);

  // Handlers get a fresh identity on every render of the tab page, so they go
  // in a ref: the layout reads the current one when a button is clicked, and
  // nothing re-renders in between.
  useEffect(() => {
    if (chrome) chrome.handlers.current = { onAddAction, onCopyClick };
  });

  // Fragment, not a bare `children`: several tabs pass a list (content plus
  // their modals), and returning that array directly triggers React's key
  // warning.
  return <>{children}</>;
};

export default ViewerPage;
